// Sketchfab Service - Handle 3D model search and download from Sketchfab
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';

/** Human-readable left-panel label; never use raw Sketchfab UID as the title. */
function sanitizeSketchfabDisplayName(name, modelUid) {
  const raw = String(name || '').trim();
  if (raw && !/^sketchfab[_-]/i.test(raw)) {
    const cleaned = raw.replace(/[\\/<>:"|?*]/g, '_').slice(0, 120).trim();
    if (cleaned) return cleaned;
  }
  return modelUid ? 'Sketchfab Model' : 'Model';
}

// Helper function to preserve material properties (enhanced for Sketchfab models)
function preserveMaterial(material) {
  if (!material) return;

  // Ensure material is rendered properly
  material.needsUpdate = true;

  // Preserve vertex colors if they exist
  if (material.vertexColors) {
    material.vertexColors = true;
  }

  // Ensure textures are properly loaded
  if (material.map) {
    material.map.needsUpdate = true;
    material.map.flipY = false; // Common fix for texture orientation
  }

  if (material.normalMap) {
    material.normalMap.needsUpdate = true;
    material.normalMap.flipY = false;
  }

  if (material.roughnessMap) {
    material.roughnessMap.needsUpdate = true;
    material.roughnessMap.flipY = false;
  }

  if (material.metalnessMap) {
    material.metalnessMap.needsUpdate = true;
    material.metalnessMap.flipY = false;
  }

  if (material.aoMap) {
    material.aoMap.needsUpdate = true;
    material.aoMap.flipY = false;
  }

  if (material.emissiveMap) {
    material.emissiveMap.needsUpdate = true;
    material.emissiveMap.flipY = false;
  }

  // Handle special material types
  if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
    // These materials should preserve PBR properties automatically
    // Ensure proper lighting
    material.envMapIntensity = 1.0;
  }

  // Ensure material is visible and properly lit
  material.visible = true;
  material.transparent = false; // Ensure it's not transparent unless it should be
  material.opacity = 1.0; // Ensure full opacity

  // Only set userData if it doesn't exist, don't override existing settings
  if (!material.userData.material_settings) {
    material.userData.material_settings = {
      albedo: material.color ? `#${material.color.getHexString()}` : '#FFFFFF',
      metalicType: false,
      emissionType: false,
      added: true,
    };
  }

  material.needsUpdate = true;
}

export class SketchfabService {
  constructor() {
    this.apiKey = 'fc7e66f5c42846189c2de0a112b765d7';
    this.baseUrl = 'https://api.sketchfab.com/v3';
  }

  // Search for 3D models on Sketchfab
  // Supports: q, count, downloadable, animated, sort_by, categories (comma-separated slugs), tags (comma-separated)
  async searchModels(query, options = {}) {
    const {
      count = 24,
      downloadable = true,
      animated = false,
      categories = '',
      tags = '',
      sort_by = 'relevance',
    } = options;

    const params = new URLSearchParams({
      type: 'models',
      q: query,
      count: Math.min(count, 24).toString(),
      downloadable: downloadable.toString(),
      animated: animated.toString(),
      sort_by,
    });
    if (categories && typeof categories === 'string') {
      params.set('categories', categories);
    }
    if (tags && typeof tags === 'string') {
      params.set('tags', tags);
    }

    try {
      const response = await fetch(`${this.baseUrl}/search?${params}&restricted=1`, {
        headers: {
          Authorization: `Token ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Sketchfab API error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return this.formatSearchResults(data.results || []);
    } catch (error) {
      console.error('Sketchfab search error:', error);
      throw new Error(`Failed to search Sketchfab: ${error.message}`);
    }
  }

  // Format search results for easier consumption
  formatSearchResults(results) {
    return results.map((model) => ({
      uid: model.uid,
      name: model.name,
      description: model.description,
      thumbnail: model.thumbnails?.images?.[0]?.url?.replace(/^http:\/\//, 'https://') || null,
      viewCount: model.viewCount,
      likeCount: model.likeCount,
      downloadCount: model.downloadCount,
      author: model.user?.displayName,
      tags: model.tags?.map((tag) => tag.name) || [],
      downloadUrl: model.downloadUrl,
      embedUrl: model.embedUrl,
      isDownloadable: model.isDownloadable,
      license: model.license?.label,
      animationCount: model.animationCount,
      vertexCount: model.vertexCount,
      faceCount: model.faceCount,
    }));
  }

  // Get download URL for a specific model
  async getModelDownloadUrl(modelUid) {
    try {
      const response = await fetch(
        `${this.baseUrl}/models/${modelUid}/download`,
        {
          headers: {
            Authorization: `Token ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        let detail = '';
        try {
          const body = await response.json();
          detail = body.detail || body.message || body.error || JSON.stringify(body);
        } catch {}
        const retryAfter = response.headers.get('Retry-After');
        console.error(`Sketchfab download URL ${response.status}:`, detail, retryAfter ? `Retry-After: ${retryAfter}s` : '');
        throw new Error(
          `Failed to get download URL: ${response.status}${detail ? ' — ' + detail : ''}`
        );
      }

      const data = await response.json();

      // Find the best quality download (usually GLTF or GLB format)
      const downloads = Object.values(data);
      const gltfDownload = downloads.find(
        (d) => d.url && (d.url.includes('.glb') || d.url.includes('.gltf'))
      );

      if (gltfDownload) {
        return gltfDownload.url;
      }

      // Fallback to any available download
      const anyDownload = downloads.find((d) => d.url);
      if (anyDownload) {
        return anyDownload.url;
      }

      throw new Error('No download URL available for this model');
    } catch (error) {
      console.error('Download URL error:', error);
      throw error;
    }
  }

  // Download and load a 3D model from Sketchfab (following handleMeshUpload pattern)
  async downloadAndLoadModel(
    modelUid,
    { scene, transformControls, selectedObject, setPolyCount },
    setPendingUploads, // New parameter to track files pending upload
    meta = {}
  ) {
    try {
      const displayName = sanitizeSketchfabDisplayName(
        meta.displayName,
        modelUid
      );

      // Clear current selection
      selectedObject = null;
      store.dispatch(selected_object(null));

      // Set loading state
      store.dispatch(setLoadingMessage(true));

      // Get the download URL
      const downloadUrl = await this.getModelDownloadUrl(modelUid);

      // Download the actual file from Sketchfab
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to download file from Sketchfab: ${response.status} ${response.statusText}`
        );
      }

      const arrayBuffer = await response.arrayBuffer();
      // Keep unique binary filename for S3 upload identity; UI uses displayName
      const fileName = `sketchfab_${modelUid}.glb`;
      const file = new File([arrayBuffer], fileName, {
        type: 'model/gltf-binary',
        lastModified: Date.now(),
      });

      // Create loaders (same as handleMeshUpload)
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      loader.setDRACOLoader(dracoLoader);

      const tempId = `sketchfab-${Date.now()}-${modelUid}`; // Create unique temporary ID
      const objectURL = URL.createObjectURL(file);

      // Load the model using THREE.js GLTFLoader
      return new Promise((resolve, reject) => {
        loader.load(
          objectURL,
          function (gltf) {
            const model = gltf.scene;

            // Compute and center the bounding box before adding to the scene (same as handleMeshUpload)
            const box = new THREE.Box3().setFromObject(model);
            const center = new THREE.Vector3();
            box.getCenter(center);

            // Preserve materials and textures (enhanced for Sketchfab models)
            model.traverse(function (child) {
              if (child.isMesh && child.geometry) {
                child.geometry.translate(-center.x, -center.y, -center.z);

                // Ensure the mesh is visible
                child.visible = true;

                if (child.material) {
                  // If it's an array of materials
                  if (Array.isArray(child.material)) {
                    child.material.forEach((mat, index) => {
                      preserveMaterial(mat);
                    });
                  } else {
                    // Single material
                    preserveMaterial(child.material);
                  }
                } else {
                }

                // Set shadow properties
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });

            // Compute how much to lift the model so its bottom rests on Y=0 (not half-buried)
            // Bounding box is recomputed AFTER geometry was centered above
            const centeredBox = new THREE.Box3().setFromObject(model);
            model.userData.groundLift = Math.max(0, -centeredBox.min.y);

            // Store file for later upload when publishing (same as handleMeshUpload)
            if (setPendingUploads) {
              setPendingUploads((prev) => [
                ...prev,
                {
                  file: file, // Now we have the actual downloaded file
                  transform: {
                    position_x: 0,
                    position_y: 0,
                    position_z: 0,
                    rotation_x: 0,
                    rotation_y: 0,
                    rotation_z: 0,
                    scale_x: 1,
                    scale_y: 1,
                    scale_z: 1,
                  },
                  tempId: tempId,
                  sketchfabUid: modelUid,
                  downloadUrl: downloadUrl,
                },
              ]);
            }

            // Set properties (same as handleMeshUpload)
            // name/temp_file_name stay unique for scene identity; file_name is left-panel label
            model.name = tempId;
            model.file_name = displayName;
            model.asset_file_name = displayName;
            model.objPath = file; // Store the actual file, not the URL
            model.objType = '3d';
            model._id = tempId;

            // Add metadata
            model.userData = {
              type: 'sketchfab',
              originalType: 'sketchfab_model',
              sketchfabUid: modelUid,
              downloadUrl: downloadUrl,
              mesh_code: `sketchfab_${modelUid}`,
              displayName,
              createdAt: Date.now(),
              material_settings: {
                albedo: '#FFFFFF', // Use white instead of transparent
                metalicType: false,
                emissionType: false,
                added: true,
              },
            };

            // Additional material processing for Sketchfab models
            model.traverse(function (child) {
              if (child.isMesh && child.material) {
                // Ensure proper material properties for Sketchfab models
                if (
                  child.material.isMeshStandardMaterial ||
                  child.material.isMeshPhysicalMaterial
                ) {
                  // Ensure the material has proper lighting
                  child.material.envMapIntensity = 1.0;

                  // If the material doesn't have a color, set a default
                  if (
                    !child.material.color ||
                    child.material.color.getHex() === 0x000000
                  ) {
                    child.material.color.setHex(0xffffff);
                  }

                  // Ensure proper roughness and metalness
                  if (child.material.roughness === undefined) {
                    child.material.roughness = 0.5;
                  }
                  if (child.material.metalness === undefined) {
                    child.material.metalness = 0.0;
                  }
                }
              }
            });

            // Set deleteType for removal tracking
            model.deleteType = 'mesh';
            model.url = tempId;
            model.originalFile = file; // Store the actual file

            resolve({
              model,
              gltf,
              animations: gltf.animations || [],
              mixer:
                gltf.animations.length > 0
                  ? new THREE.AnimationMixer(model)
                  : null,
              tempId,
              file, // Return the downloaded file
              displayName,
            });
          },
          (progress) => {
            const percentComplete = (progress.loaded / progress.total) * 100;
          },
          function (error) {
            console.error(error);
            toast.error('Failed to load Sketchfab model');
            store.dispatch(setLoadingMessage(false));
            reject(new Error(`Failed to load 3D model: ${error.message}`));
          }
        );
      });
    } catch (error) {
      console.error('Download and load error:', error);
      toast.error('Failed to download Sketchfab model');
      store.dispatch(setLoadingMessage(false));
      throw error;
    }
  }

  // Add model to scene and Redux store (following handleMeshUpload pattern)
  async addModelToScene(
    modelUid,
    { scene, transformControls, selectedObject, setPolyCount },
    setPendingUploads, // New parameter to track files pending upload
    meta = {}
  ) {
    try {
      const { model, gltf, animations, mixer, tempId, file, displayName } =
        await this.downloadAndLoadModel(
          modelUid,
          {
            scene,
            transformControls,
            selectedObject,
            setPolyCount,
          },
          setPendingUploads,
          meta
        );

      const panelName =
        displayName ||
        sanitizeSketchfabDisplayName(meta.displayName, modelUid);

      // Apply ground lift (so model bottom sits on Y=0 instead of being half-buried)
      // and spread X/Z so consecutive assets don't all pile up at the origin
      const groundLift = model.userData.groundLift || 0;
      const existingSketchfabMeshes = scene.children.filter(
        (c) => c.userData?.type === 'sketchfab' || c.userData?.originalType === 'sketchfab_model'
      );
      const meshCount = existingSketchfabMeshes.length;
      if (meshCount === 0) {
        model.position.set(0, groundLift, 0);
      } else {
        // Golden-angle spiral: evenly fans models outward without clustering
        const angle = meshCount * 2.399; // ~137.5° in radians
        const distance = 1.5 + Math.floor(meshCount / 5) * 1.5;
        model.position.set(
          parseFloat((Math.cos(angle) * distance).toFixed(2)),
          groundLift,
          parseFloat((Math.sin(angle) * distance).toFixed(2))
        );
      }

      // Add to scene (same as handleMeshUpload)
      store.dispatch(setPushIntersectedObject(model));
      scene.add(model);
      setPolyCount((prevCount) => prevCount + 1);

      // Create a plain serializable record for Redux (not the live Three.js model object)
      // file_name drives AssetList left-panel label — use human Sketchfab title
      const sketchfabReduxRecord = {
        _id: tempId,
        name: tempId,
        temp_file_name: tempId,
        file_name: panelName,
        asset_file_name: panelName,
        url: tempId,
        source: 'sketchfab',
        glb_type: 'normal',
        deleteType: 'mesh',
        objType: '3d',
        sketchfabUid: modelUid,
        position: {
          x: model.position.x,
          y: model.position.y,
          z: model.position.z,
        },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        parent: null,
        userData: {
          type: 'sketchfab',
          sketchfabUid: modelUid,
          displayName: panelName,
          mesh_code: `sketchfab_${modelUid}`,
        },
        objPath: file,
      };

      store.dispatch(add_new_assets({ type: 'mesh', data: sketchfabReduxRecord }));
      store.dispatch(set_is_glb_image_editoropen(true));

      // Select object in scene (same as handleMeshUpload)
      selectObjectInScene(tempId);
      toast.success(`"${panelName}" added to scene`);
      store.dispatch(
        addModel({
          url: URL.createObjectURL(file),
          name: panelName,
        })
      );
      store.dispatch(addSceneMesh(sketchfabReduxRecord));
      store.dispatch(setLoadingMessage(false));

      return {
        success: true,
        model,
        gltf,
        animations,
        mixer,
        tempId,
        file,
        displayName: panelName,
        message: `Successfully added "${panelName}" to scene`,
      };
    } catch (error) {
      console.error('Add model to scene error:', error);
      store.dispatch(setLoadingMessage(false));
      return {
        success: false,
        error: error.message,
        message: `Failed to add Sketchfab model: ${error.message}`,
      };
    }
  }

  // Search and add a matching model.
  // Always skips UIDs already in the grid unless listed in allowUids.
  // Results must match the subject (e.g. "car" → name/tags contain car/vehicle).
  async searchAndAddModel(
    query,
    { scene, transformControls, selectedObject, setPolyCount },
    setPendingUploads,
    options = {}
  ) {
    try {
      const {
        isGenericModelQuery,
        getVarietyQueries,
      } = await import('./librarySearchService');
      const {
        normalizeModelSearchQuery,
        itemMatchesSubject,
        subjectRelevanceScore,
      } = await import('./modelRelevance');

      const allow = new Set(
        (options.allowUids || []).map((id) => String(id))
      );
      const exclude = new Set(
        (options.excludeUids || [])
          .map((id) => String(id))
          .filter((id) => !allow.has(id))
      );
      const requireUnique = options.requireUnique !== false;
      const subject =
        normalizeModelSearchQuery(query) || String(query || '').trim();
      const generic =
        options.preferVariety === true || isGenericModelQuery(subject);

      const searchTerms = generic
        ? getVarietyQueries(options.varietyOffset || 0, 10)
        : [subject, `${subject} low poly`, `${subject} vehicle`].filter(
            Boolean
          );

      let selectedModel = null;
      let usedTerm = subject;

      for (const term of searchTerms) {
        const searchResults = await this.searchModels(term, {
          count: 24,
          downloadable: true,
          sort_by: 'relevance',
        });

        const downloadableModels = (searchResults || []).filter(
          (model) => model.isDownloadable && model.uid
        );

        // Keep only models that actually match the search subject
        const relevant = downloadableModels
          .filter(
            (model) =>
              allow.has(String(model.uid)) || itemMatchesSubject(model, term)
          )
          .map((model) => ({
            model,
            score: subjectRelevanceScore(model, term),
          }))
          .filter((row) => row.score > 0 || allow.has(String(row.model.uid)))
          .sort((a, b) => b.score - a.score)
          .map((row) => row.model);

        const unused = relevant.filter(
          (model) =>
            !exclude.has(String(model.uid)) || allow.has(String(model.uid))
        );

        const preferred = unused.find((m) => allow.has(String(m.uid)));
        if (preferred) {
          selectedModel = preferred;
          usedTerm = term;
          break;
        }

        if (unused.length) {
          // Specific: best relevance (index 0). Generic: rotate among relevant unused.
          if (generic && unused.length > 1) {
            const idx =
              (Number(options.varietyOffset) || 0) % unused.length;
            selectedModel = unused[idx];
          } else {
            selectedModel = unused[0];
          }
          usedTerm = term;
          break;
        }
      }

      if (!selectedModel) {
        throw new Error(
          requireUnique
            ? `No new unique "${subject}" model found — try a more specific name`
            : `No downloadable "${subject}" models found on Sketchfab`
        );
      }

      const displayName = sanitizeSketchfabDisplayName(
        selectedModel.name,
        selectedModel.uid
      );

      const result = await this.addModelToScene(
        selectedModel.uid,
        {
          scene,
          transformControls,
          selectedObject,
          setPolyCount,
        },
        setPendingUploads,
        { displayName }
      );

      return {
        ...result,
        modelInfo: {
          ...selectedModel,
          name: displayName,
          searchTerm: usedTerm,
        },
        message: `Added "${displayName}" by ${selectedModel.author || 'Sketchfab'} to scene`,
      };
    } catch (error) {
      console.error('Search and add model error:', error);
      store.dispatch(setLoadingMessage(false));
      return {
        success: false,
        error: error.message,
        message: `Failed to find and add model for "${query}": ${error.message}`,
      };
    }
  }

  // Get model details by UID
  async getModelDetails(modelUid) {
    try {
      const response = await fetch(`${this.baseUrl}/models/${modelUid}`, {
        headers: {
          Authorization: `Token ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get model details: ${response.status}`);
      }

      const data = await response.json();
      return this.formatSearchResults([data])[0];
    } catch (error) {
      console.error('Get model details error:', error);
      throw error;
    }
  }

  // Validate API key
  async validateApiKey() {
    try {
      const response = await fetch(`${this.baseUrl}/me`, {
        headers: {
          Authorization: `Token ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('API key validation error:', error);
      return false;
    }
  }
}

// Export singleton instance
export const sketchfabService = new SketchfabService();

// Helper functions for integration with existing code (following handleMeshUpload pattern)
export async function addSketchfabModel(
  query,
  scene,
  store,
  options = {},
  setPendingUploads = null
) {
  // Backward compatibility wrapper - convert old signature to new signature
  const transformControls = null; // Not available in commandTools context
  const selectedObject = null; // Not available in commandTools context
  const setPolyCount = () => {}; // Dummy function since not available in commandTools context

  return await sketchfabService.searchAndAddModel(
    query,
    {
      scene,
      transformControls,
      selectedObject,
      setPolyCount,
    },
    setPendingUploads
  );
}

export async function searchSketchfabModels(query, options = {}) {
  return await sketchfabService.searchModels(query, options);
}

export async function loadSketchfabModelByUid(
  modelUid,
  scene,
  store,
  options = {},
  setPendingUploads = null
) {
  // Backward compatibility wrapper - convert old signature to new signature
  const transformControls = null; // Not available in commandTools context
  const selectedObject = null; // Not available in commandTools context
  const setPolyCount = () => {}; // Dummy function since not available in commandTools context

  return await sketchfabService.addModelToScene(
    modelUid,
    {
      scene,
      transformControls,
      selectedObject,
      setPolyCount,
    },
    setPendingUploads
  );
}

// New functions with full parameter support (for future use)
export async function addSketchfabModelWithFullParams(
  query,
  { scene, transformControls, selectedObject, setPolyCount },
  setPendingUploads
) {
  return await sketchfabService.searchAndAddModel(
    query,
    {
      scene,
      transformControls,
      selectedObject,
      setPolyCount,
    },
    setPendingUploads
  );
}

export async function loadSketchfabModelByUidWithFullParams(
  modelUid,
  { scene, transformControls, selectedObject, setPolyCount },
  setPendingUploads
) {
  return await sketchfabService.addModelToScene(
    modelUid,
    {
      scene,
      transformControls,
      selectedObject,
      setPolyCount,
    },
    setPendingUploads
  );
}
